import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, Link2, Trash2, ExternalLink, AlertCircle, Edit3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function TrafficAnalytics({ user, selectedClient }) {
  const firmId = selectedClient?.firmId || selectedClient?.firm_id || user?.firm_id || user?.firm?.id;
  const canEdit = ['super_admin', 'internal_team'].includes(user?.role);

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [titleInput, setTitleInput] = useState('Reporting Dashboard');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchDashboard = useCallback(async () => {
    if (!firmId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/traffic-analytics/${firmId}/reporting-dashboard`, { headers: authHeaders() });
      setDashboard(res.data);
      setUrlInput(res.data?.dashboard_url || '');
      setTitleInput(res.data?.title || 'Reporting Dashboard');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load dashboard');
    } finally { setLoading(false); }
  }, [firmId]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleSave = async () => {
    if (!urlInput.trim()) { setError('Please enter a dashboard URL.'); return; }
    setSaving(true); setError('');
    try {
      const res = await axios.put(`${API}/traffic-analytics/${firmId}/reporting-dashboard`,
        { dashboard_url: urlInput.trim(), title: titleInput.trim() || 'Reporting Dashboard' },
        { headers: authHeaders() });
      setDashboard(res.data);
      setEditing(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save dashboard URL.');
    } finally { setSaving(false); }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove the embedded reporting dashboard?')) return;
    await axios.delete(`${API}/traffic-analytics/${firmId}/reporting-dashboard`, { headers: authHeaders() });
    setDashboard(null); setUrlInput(''); setEditing(false);
  };

  const hasDashboard = !!dashboard?.dashboard_url;

  return (
    <div className="space-y-6 animate-fadeInUp" data-testid="traffic-analytics-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Traffic Analysis</h1>
          <p className="text-slate-500 text-lg">View the live reporting dashboard prepared by your team.</p>
        </div>
        {canEdit && hasDashboard && !editing && (
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="edit-dashboard-url-btn">
              <Edit3 className="w-4 h-4" /> Edit Dashboard URL
            </button>
            <button onClick={handleRemove} className="flex items-center gap-2 px-4 py-2.5 bg-white border border-rose-200 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50" data-testid="remove-dashboard-url-btn">
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          </div>
        )}
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</div>}

      {loading ? (
        <div className="bg-white/70 rounded-2xl border border-white/50 p-12 text-center text-slate-500">Loading…</div>
      ) : !hasDashboard || editing ? (
        canEdit ? (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-8 soft-shadow-lg" data-testid="dashboard-url-form">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                <Link2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-lg">{editing ? 'Edit Reporting Dashboard URL' : 'Add Reporting Dashboard URL'}</h3>
                <p className="text-slate-500 text-sm">Paste a Looker Studio / Google Data Studio embed URL for this client.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Dashboard Title</label>
                <input
                  type="text" value={titleInput} onChange={e => setTitleInput(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  data-testid="dashboard-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Dashboard Embed URL</label>
                <input
                  type="url" value={urlInput} onChange={e => setUrlInput(e.target.value)}
                  placeholder="https://lookerstudio.google.com/embed/reporting/…"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  data-testid="dashboard-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">Tip: In Looker Studio open Share → Embed → copy the embed URL.</p>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50" data-testid="save-dashboard-url-btn">
                  {saving ? 'Saving…' : 'Save Dashboard URL'}
                </button>
                {editing && (
                  <button onClick={() => { setEditing(false); setUrlInput(dashboard?.dashboard_url || ''); }} className="px-5 py-2.5 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 p-16 text-center soft-shadow" data-testid="no-dashboard-state">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No reporting dashboard assigned yet.</h3>
            <p className="text-slate-500">Your account manager will add your reporting dashboard here shortly.</p>
          </div>
        )
      ) : (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden soft-shadow-lg" data-testid="embedded-dashboard">
          <div className="px-6 py-4 border-b border-slate-200/50 bg-gradient-to-r from-slate-50/50 to-white/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-violet-600" />
              <h3 className="font-semibold text-slate-800">{dashboard.title || 'Reporting Dashboard'}</h3>
            </div>
            <a href={dashboard.dashboard_url} target="_blank" rel="noopener noreferrer" className="text-sm text-violet-600 hover:underline flex items-center gap-1" data-testid="open-dashboard-link">
              Open in new tab <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="relative" style={{ paddingBottom: '60%' }}>
            <iframe
              title={dashboard.title || 'Reporting Dashboard'}
              src={dashboard.dashboard_url}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allowFullScreen
              data-testid="dashboard-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
